import { useState, useEffect, useCallback } from 'react';
import { clientsAPI } from '../../api/clients';
import { contactsAPI } from '../../api/contacts';
import { quotesAPI } from '../../api/quotes';
import { 
  Users, Mail, DollarSign, TrendingUp, 
  Clock, Activity, Percent, RefreshCw 
} from 'lucide-react';
import Loader from '../common/Loader';
import '../../styles/admin/AdminStats.css';

export default function AdminStats() {
  const [clientStats, setClientStats] = useState(null);
  const [contactStats, setContactStats] = useState(null);
  const [quoteStats, setQuoteStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week'); // 'day', 'week', 'month'

  const generateRecentActivity = useCallback((clientsData, contactsData) => {
    const activity = [];
    
    // Ajouter des contacts récents
    if (contactsData?.recentContacts && Array.isArray(contactsData.recentContacts)) {
      contactsData.recentContacts.slice(0, 3).forEach(contact => {
        activity.push({
          type: 'contact',
          icon: Mail,
          color: 'info',
          content: `Nouveau message de ${contact.name || 'contact'}`,
          time: new Date(contact.createdAt || Date.now()),
          data: contact
        });
      });
    }

    // Ajouter des clients récents
    if (clientsData?.recentClients && Array.isArray(clientsData.recentClients)) {
      clientsData.recentClients.slice(0, 3).forEach(client => {
        activity.push({
          type: 'client',
          icon: Users,
          color: 'success',
          content: `Nouveau client: ${client.name || 'client'}`,
          time: new Date(client.createdAt || Date.now()),
          data: client
        });
      });
    }

    // Trier par date
    activity.sort((a, b) => b.time - a.time);
    
    // Limiter à 5 activités
    setRecentActivity(activity.slice(0, 5));
  }, []);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      
      // Charger toutes les stats en parallèle
      const [clientsRes, contactsRes, quotesRes] = await Promise.all([
        clientsAPI.getStats().catch(() => ({ data: null })),
        contactsAPI.getStats().catch(() => ({ data: null })),
        quotesAPI.getStats().catch(() => ({ data: null }))
      ]);

      setClientStats(clientsRes?.data || null);
      setContactStats(contactsRes?.data || null);
      setQuoteStats(quotesRes?.data || null);

      // Générer l'activité récente
      generateRecentActivity(clientsRes?.data, contactsRes?.data);
      
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    } finally {
      setLoading(false);
    }
  }, [generateRecentActivity]);

  useEffect(() => {
    loadStats();
    
    // Rafraîchir automatiquement toutes les 5 minutes
    const interval = setInterval(() => {
      loadStats();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [loadStats]);

  const calculateGrowth = (current, previous) => {
    if (!previous || previous === 0) return 100;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString('fr-FR');
  };

  if (loading && !clientStats && !contactStats && !quoteStats) {
    return <Loader />;
  }

  return (
    <div className="admin-stats-section">
      {/* En-tête */}
      <div className="stats-header">
        <div>
          <h2>Tableau de Bord</h2>
          <p className="subtitle">Aperçu de votre activité</p>
        </div>
        
        <div className="time-range-selector">
          <select 
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="time-select"
          >
            <option value="day">Aujourd'hui</option>
            <option value="week">Cette semaine</option>
            <option value="month">Ce mois</option>
            <option value="year">Cette année</option>
          </select>
          <Clock size={18} />
        </div>
      </div>

      {/* Cartes de statistiques */}
      <div className="stats-overview">
        <div className="stats-grid">
          {/* Clients */}
          <div className="stat-card client-stat">
            <div className="stat-header">
              <div className="stat-icon">
                <Users size={24} />
              </div>
              <span className="stat-label">Clients</span>
            </div>
            <div className="stat-content">
              <h3>{formatNumber(clientStats?.total || 0)}</h3>
              <div className="stat-trend">
                <TrendingUp size={16} />
                <span className="trend-positive">
                  {calculateGrowth(clientStats?.total || 0, clientStats?.previousTotal || 0)}%
                </span>
                <span className="trend-label">vs période précédente</span>
              </div>
            </div>
            <div className="stat-footer">
              <div className="stat-detail">
                <span>Leads:</span>
                <strong>{formatNumber(clientStats?.byStatus?.find(s => s?._id === 'lead')?.count || 0)}</strong>
              </div>
              <div className="stat-detail">
                <span>En cours:</span>
                <strong>{formatNumber(clientStats?.byStatus?.find(s => s?._id === 'in_progress')?.count || 0)}</strong>
              </div>
            </div>
          </div>

          {/* Contacts */}
          <div className="stat-card contact-stat">
            <div className="stat-header">
              <div className="stat-icon">
                <Mail size={24} />
              </div>
              <span className="stat-label">Contacts</span>
            </div>
            <div className="stat-content">
              <h3>{formatNumber(contactStats?.total || 0)}</h3>
              <div className="stat-trend">
                <TrendingUp size={16} />
                <span className="trend-positive">
                  {calculateGrowth(contactStats?.recent || 0, contactStats?.previousRecent || 0)}%
                </span>
                <span className="trend-label">nouveaux (7j)</span>
              </div>
            </div>
            <div className="stat-footer">
              <div className="stat-detail">
                <span>Nouveaux:</span>
                <strong>{formatNumber(contactStats?.byStatus?.find(s => s?._id === 'new')?.count || 0)}</strong>
              </div>
              <div className="stat-detail">
                <span>A répondre:</span>
                <strong>{formatNumber(contactStats?.byStatus?.find(s => s?._id === 'read')?.count || 0)}</strong>
              </div>
            </div>
          </div>

          {/* Devis */}
          <div className="stat-card quote-stat">
            <div className="stat-header">
              <div className="stat-icon">
                <DollarSign size={24} />
              </div>
              <span className="stat-label">Devis</span>
            </div>
            <div className="stat-content">
              <h3>{formatNumber(quoteStats?.total || 0)}</h3>
              <div className="stat-trend">
                <TrendingUp size={16} />
                <span className="trend-positive">
                  {calculateGrowth(quoteStats?.total || 0, quoteStats?.previousTotal || 0)}%
                </span>
                <span className="trend-label">vs période précédente</span>
              </div>
            </div>
            <div className="stat-footer">
              <div className="stat-detail">
                <span>Envoyés:</span>
                <strong>{formatNumber(quoteStats?.byStatus?.find(s => s?._id === 'sent')?.count || 0)}</strong>
              </div>
              <div className="stat-detail">
                <span>Acceptés:</span>
                <strong>{formatNumber(quoteStats?.byStatus?.find(s => s?._id === 'accepted')?.count || 0)}</strong>
              </div>
            </div>
          </div>

          {/* Taux de conversion */}
          <div className="stat-card conversion-stat">
            <div className="stat-header">
              <div className="stat-icon">
                <Percent size={24} />
              </div>
              <span className="stat-label">Conversion</span>
            </div>
            <div className="stat-content">
              <h3>{(quoteStats?.conversionRate || 0).toFixed(1)}%</h3>
              <div className="stat-trend">
                <Activity size={16} />
                <span className="trend-neutral">
                  Taux de conversion
                </span>
              </div>
            </div>
            <div className="stat-footer">
              <div className="stat-detail">
                <span>Contacts → Devis:</span>
                <strong>{((contactStats?.total && quoteStats?.total) ? 
                  (quoteStats.total / contactStats.total * 100).toFixed(1) : 0)}%
                </strong>
              </div>
              <div className="stat-detail">
                <span>Devis → Projets:</span>
                <strong>{formatNumber(clientStats?.byStatus?.find(s => s?._id === 'in_progress')?.count || 0)}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Graphiques et détails */}
      <div className="stats-details">
        {/* Répartition clients par statut */}
        <div className="stat-chart">
          <div className="chart-header">
            <h3>Clients par statut</h3>
            <div className="chart-legend">
              {clientStats?.byStatus?.map(stat => (
                <div key={stat._id} className="legend-item">
                  <span 
                    className="legend-color" 
                    style={{ backgroundColor: getStatusColor(stat._id) }}
                  ></span>
                  <span>{formatStatus(stat._id)}</span>
                  <span className="legend-count">{stat.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="chart-bars">
            {clientStats?.byStatus?.map((stat) => (
              <div key={stat._id} className="chart-bar">
                <div className="bar-label">
                  <span>{formatStatus(stat._id)}</span>
                  <span>{stat.count}</span>
                </div>
                <div className="bar-container">
                  <div 
                    className="bar-fill" 
                    style={{ 
                      width: `${clientStats.total ? (stat.count / clientStats.total) * 100 : 0}%`,
                      backgroundColor: getStatusColor(stat._id)
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Types de projets */}
        <div className="stat-chart">
          <div className="chart-header">
            <h3>Types de projets</h3>
          </div>
          <div className="project-types">
            {clientStats?.byProjectType?.map((stat) => (
              <div key={stat._id} className="project-type-item">
                <div className="project-type-info">
                  <span className="project-name">{formatProjectType(stat._id)}</span>
                  <span className="project-count">{stat.count}</span>
                </div>
                <div className="project-percentage">
                  {clientStats.total ? ((stat.count / clientStats.total) * 100).toFixed(1) : 0}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Activité récente */}
      <div className="recent-activity">
        <div className="activity-header">
          <h3>
            <Activity size={20} />
            Activité récente
          </h3>
          <button 
            onClick={loadStats}
            className="btn-refresh"
            title="Rafraîchir"
          >
            <RefreshCw size={16} />
          </button>
        </div>
        
        <div className="activity-list">
          {recentActivity.length === 0 ? (
            <div className="no-activity">
              <Activity size={24} />
              <p>Aucune activité récente</p>
            </div>
          ) : (
            recentActivity.map((activity, index) => {
              const Icon = activity.icon;
              return (
                <div key={index} className={`activity-item ${activity.color}`}>
                  <div className="activity-icon">
                    <Icon size={16} />
                  </div>
                  <div className="activity-content">
                    <p>{activity.content}</p>
                    <span className="activity-time">
                      {formatRelativeTime(activity.time)}
                    </span>
                  </div>
                  {activity.data?.email && (
                    <a 
                      href={`mailto:${activity.data.email}`}
                      className="activity-action"
                      title="Envoyer un email"
                    >
                      <Mail size={14} />
                    </a>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// Fonctions utilitaires
function formatRelativeTime(date) {
  const now = new Date();
  const diffMs = now - date;
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) return 'À l\'instant';
  if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;
  if (diffHours < 24) return `Il y a ${diffHours} h`;
  if (diffDays < 7) return `Il y a ${diffDays} j`;
  
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit'
  });
}

function formatStatus(status) {
  const statusMap = {
    'lead': 'Lead',
    'consultation': 'Consultation',
    'quote_sent': 'Devis envoyé',
    'in_progress': 'En cours',
    'completed': 'Terminé',
    'archived': 'Archivé'
  };
  return statusMap[status] || status;
}

function getStatusColor(status) {
  const colors = {
    'lead': '#3B82F6',
    'consultation': '#F59E0B',
    'quote_sent': '#8B5CF6',
    'in_progress': '#10B981',
    'completed': '#6B7280',
    'archived': '#EF4444'
  };
  return colors[status] || '#6B7280';
}

function formatProjectType(type) {
  const typeMap = {
    'website': 'Site Web',
    'ecommerce': 'E-commerce',
    'marketing': 'Marketing',
    'print': 'Impression',
    'crm': 'CRM',
    'maintenance': 'Maintenance',
    'other': 'Autre',
    '': 'Non spécifié'
  };
  return typeMap[type] || type;
}